import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format, set } from "date-fns";
import { MenuItem, Tooltip, CircularProgress, Typography } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { Calendar } from "iconsax-react";
import { KeyboardArrowDown } from "@mui/icons-material";

import ShipeeziGrid from "Components/AppGrid/shipeezi-new-grid";
import BadgeSubStatus from "Components/Generic/BadgeSubStatus";
import IconPdfFile from "Components/Generic/Icons/IconPDFFile";
import InputDateRange from "Components/Form/Input/DateRange";
import {
  PeriodOptions,
  DateSelectorFilter,
} from "../ManagementReportScreen/components/DateSelector";

import { useAppState } from "Context/StateContext";
import QuoteService from "Services/quote.service";
import StatusesService from "Services/statuses.service";
import JobTypeService from "Services/job.type.service";
import { handlesGrid } from "Util/handlesGrid";
import { DefaultDateOptionsColumn, DefaultPeriodOptionsColumn } from "Constants";
import { SubStatusUsedBy, Status, Mode, ReportName } from "Util/enums";
import { Filters, Searches } from "Models/shipeezi.table";
import { Customer } from "Models/customer";
import { Sales } from "Models/sales";
import { IconWrapper } from "./styles";
import { capitalizeFirstCharacter } from "Util/Format";
import { EmptyDiv, ExportButton, ExportMenu } from "../styled";
import { Color } from "Components/Form/Button";
import GenerateReportModal, {
  GenerateReportModalHandler,
} from "../../../Shared/GenerateReportModal";

const today = new Date();

export const SurveyReportScreen = ({ reportId }: { reportId?: number }) => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const defaultEndDate = new Date();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [period, setPeriod] = useState<DefaultPeriodOptionsColumn>(
    DefaultPeriodOptionsColumn.CUSTOM_PERIOD
  );
  const [startDate, setStartDate] = useState<Date | undefined>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultEndDate);
  const [selecteds, setSelecteds] = useState<number[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const modalRef = useRef<GenerateReportModalHandler>(null);

  const { handleCurrentPage, handleFilter, handlePageSize, handleSearch } = handlesGrid({
    setFilters,
    setPage,
    setPageSize,
    setSearches,
  });

  const { data: quoteList, isFetching: loadingList } = useQuery({
    queryKey: ["quote.list", page, pageSize, filters, searches, startDate, endDate],
    queryFn: async () => {
      const response = await QuoteService.getAll({
        companyId: currentUser?.companyId,
        page,
        pageSize,
        startDate,
        endDate,
        userId: currentUser?.id,
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
        isInReportPage: true,
        ...filters,
      });

      if (response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.id,
    refetchOnWindowFocus: false,
  });

  const { data: quoteTotal, isFetching: loadingTotal } = useQuery({
    queryKey: ["quote.total", filters, searches, startDate, endDate],
    queryFn: async () => {
      const response = await QuoteService.count({
        companyId: currentUser?.companyId,
        userId: currentUser?.id,
        startDate,
        endDate,
        searchByOr: searches?.length ? JSON.stringify(searches) : "",
        isInReportPage: true,
        ...filters,
      });

      if (response?.data) return response.data;

      return 0;
    },
    enabled: !!currentUser?.id,
    refetchOnWindowFocus: false,
  });

  const { data: jobStatuses } = useQuery({
    queryKey: ["jobStatus.list"],
    queryFn: async () => {
      const response = await StatusesService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        usedBy: SubStatusUsedBy.ESTIMATES,
      });

      if (response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const { data: jobTypes } = useQuery({
    queryKey: ["jobType.list"],
    queryFn: async () => {
      const response = await JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response?.data) return response.data;

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const renderExportButton = () => {
    if (selecteds && selecteds?.length > 0) {
      return (
        <>
          <ExportButton
            customColor={Color.INFO}
            endIcon={<KeyboardArrowDown htmlColor="#667085" />}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            {translate("exportAs")}
          </ExportButton>
          <ExportMenu
            anchorEl={menuAnchor}
            keepMounted
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                modalRef.current?.handleOpenModal(selecteds, true);
                setMenuAnchor(null);
              }}
            >
              {translate("pdf")}
            </MenuItem>
          </ExportMenu>
        </>
      );
    }

    return null;
  };

  const getLastDaysLabel = (days: number) => {
    return `${capitalizeFirstCharacter(translate("last"))} ${days} ${translate(
      "days"
    ).toLocaleLowerCase()}`;
  };

  const periodOptions: PeriodOptions[] = [
    {
      name: getLastDaysLabel(7),
      column: DefaultPeriodOptionsColumn.LAST_WEEK,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 7,
    },
    {
      name: getLastDaysLabel(30),
      column: DefaultPeriodOptionsColumn.LAST_MONTH,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 30,
    },
    {
      name: getLastDaysLabel(90),
      column: DefaultPeriodOptionsColumn.LAST_QUARTER,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 90,
    },
    {
      name: getLastDaysLabel(365),
      column: DefaultPeriodOptionsColumn.LAST_YEAR,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      totalDays: 365,
    },
    {
      name: translate("lifetime"),
      column: DefaultPeriodOptionsColumn.LIFETIME,
      type: DefaultDateOptionsColumn.HIDE_DATE_SELECTOR,
      description: translate("reportLifetimeData"),
    },
    {
      name: translate("dateRange"),
      column: DefaultPeriodOptionsColumn.CUSTOM_PERIOD,
      type: DefaultDateOptionsColumn.DATE_RANGE,
      icon: <Calendar size={12} color={colors.gray800} />,
    },
  ];

  const setStartEndDates = (period: string, startDate: Date, endDate: Date) => {
    switch (period) {
      case DefaultPeriodOptionsColumn.LAST_WEEK:
        setStartDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_MONTH:
        setStartDate(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_QUARTER:
        setStartDate(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LAST_YEAR:
        setStartDate(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.LIFETIME:
        setStartDate(new Date(Date.now() - 20 * 365 * 24 * 60 * 60 * 1000));
        setEndDate(new Date());
        break;
      case DefaultPeriodOptionsColumn.CUSTOM_DATE:
        setStartDate(startDate || "");
        setEndDate(startDate || "");
        break;
      case DefaultPeriodOptionsColumn.CUSTOM_PERIOD:
        setStartDate(startDate || "");
        setEndDate(endDate || "");
        break;
      default:
        setStartDate(undefined);
        setEndDate(undefined);
    }
  };

  const onChangePeriod = (period: DefaultPeriodOptionsColumn) => {
    setStartEndDates(period, startDate ?? defaultStartDate, endDate ?? defaultEndDate);
    setPeriod(period);
    setPage(0);
  };

  const renderSelectPeriodFilters = () => {
    return <DateSelectorFilter options={periodOptions} value={period} onChange={onChangePeriod} />;
  };

  const renderInputDateRange = () => {
    switch (period) {
      case DefaultPeriodOptionsColumn.CUSTOM_PERIOD:
        const getStartDate = () => {
          if (startDate === undefined) return new Date(today.getFullYear(), today.getMonth(), 1);
          if (startDate) return new Date(startDate);

          return undefined;
        };

        const getEndDate = () => {
          if (endDate === undefined) return today;
          if (endDate) return new Date(endDate);

          return undefined;
        };

        return (
          <InputDateRange
            pStartDate={getStartDate()}
            pEndDate={getEndDate()}
            onChange={(range) => {
              setStartDate(range?.startDate);
              setEndDate(range?.endDate);
            }}
          />
        );
      default:
        return null;
    }
  };

  const getQuoteFilters = () => {
    return {
      categories: [
        {
          name: translate("type"),
          options: jobTypes?.map((jobType) => jobType?.name || ""),
        },
        {
          name: translate("status"),
          column: "sub_status",
          options: jobStatuses?.map((jobStatus) => jobStatus?.name || ""),
        },
        {
          name: translate("mode"),
          column: "mode",
          options: [Mode.Air, Mode.Road, Mode.Sea, Mode.Rail],
        },
      ],
      onChange: handleFilter,
    };
  };

  const showBadgeSubStatus = (value?: string) => {
    return <BadgeSubStatus value={value} />;
  };

  const handleGenerateReport = (rowId: number) => {
    return (
      <Tooltip title={translate("generateReport")} placement="top">
        <IconWrapper
          onClick={() => {
            modalRef.current?.handleOpenModal([rowId], true);
          }}
        >
          <IconPdfFile height={18} width={18} color={colors.error400} />
        </IconWrapper>
      </Tooltip>
    );
  };

  const renderEmptyDiv = () => {
    return (
      <EmptyDiv>
        <Typography
          fontFamily="Manrope"
          fontWeight={400}
          fontSize="16px"
          color="#9CA6AF"
          lineHeight="18px"
          marginBottom="28px"
        >
          {translate("itFeelsEmptyRightNow")}
        </Typography>
      </EmptyDiv>
    );
  };

  return (
    <>
      <ShipeeziGrid
        columns={[
          { name: "reference", title: translate("reference") },
          {
            name: "customer",
            title: translate("customer"),
            formatText: (customer: Customer) => (customer?.fullName ? customer.fullName : ""),
          },
          { name: "type", title: translate("type") },
          {
            name: "updatedAt",
            title: translate("date"),
            hideSearch: true,
            hideSort: true,
            formatComponent: (_, row: Sales) => {
              if (row.tasks?.length) {
                const sortedTasks = row.tasks.sort((a, b) => a.id! - b.id!);
                return format(new Date(sortedTasks[0].startDate), "dd/MM/yyyy");
              }

              return "";
            },
          },
          {
            name: "mode",
            title: translate("mode"),
            formatComponent: (value: string) =>
              value ? translate(value.toLowerCase()) : "",
          },
          {
            name: "subStatus",
            title: translate("status"),
            formatComponent: showBadgeSubStatus,
            hideSearch: true,
          },
        ]}
        data={quoteList || []}
        totalItems={quoteTotal || 0}
        loadingData={loadingList || loadingTotal}
        namePage={translate("quotes")}
        emptyData={() => renderEmptyDiv()}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        onChangePageSize={handlePageSize}
        currentPage={page}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        selecteds={selecteds}
        handleSelect={setSelecteds}
        generalActions={{
          orderBy: {
            right: ["export"],
            left: ["handleSelectPeriod", "dateSelector", "handleSearch", "handleFilterBy"],
          },
          handleSearch: {
            onSearch: (filters) => handleSearch(filters),
          },
          handleFilterBy: getQuoteFilters(),
          handleSelectPeriod: renderSelectPeriodFilters(),
          dateSelector: renderInputDateRange(),
          export: renderExportButton(),
        }}
        rowActions={{
          orderBy: ["generateReport"],
        }}
        rowActionsExtra={[
          {
            title: translate("generateReport"),
            key: "generateReport",
            buttonAndAction: handleGenerateReport,
          },
        ]}
        positionActionsColumn="right"
      />

      <GenerateReportModal
        ref={modalRef}
        jobs={quoteList}
        report={{ name: ReportName.SURVEY_REPORT, reportId }}
      />
    </>
  );
};
